// Automation intent builders (server-authoritative trigger ops, P6). Each
// returns a { domain:'automation', kind, ... } object the server's match:intent
// handler routes into automationService. Keep payload shapes in lockstep with
// valkenhall-server/src/match/automationService.js.
//
// Only the SELF-CONTAINED silent ops of a fired trigger (status toggle,
// this-turn mark, rubble conversion, dispel-self) move server-side here. Hidden-
// info ops (draw) and interactive ops (interrogator_dodge, discard_spell) stay on
// the acting client until their durable prompt lands. The server no-ops them so
// the acting client still resolves them locally with no behavior change.

// Ops the SERVER resolves SILENTLY (no player choice). The client delegates a
// fully-silent automation entry by emitting one triggerIntent and SKIPPING local
// resolution, so the effect resolves once (server diff), not twice. Mirror of the
// server's SERVER_SILENT_OPS ∪ SERVER_DISPATCH_OPS. Keep in lockstep.
export const SERVER_SILENT_OPS = new Set([
  'apply_status_to_self',
  'mark_used_this_turn',
  'untap_self',
  'dispel_self',
  'return_self_to_hand',
  'damage_watch_target',
  'all_players_draw_per_scope',
  'sacrifice_self_draw',
  'draw_for_bearer',
  'kill_by_power_in_scope',
  'submerge_each_in_scope',
  'force_region_watch_target',
  'convert_each_in_scope_to_rubble',
  'kill_each',
  'damage_each',
  'return_each_to_hand',
  'banish_each',
  'summon_token_at_covered',
  'teleport_each_to_source',
  'grant_keyword_to_watch_target',
  'destroy_equipped_in_scope',
  'play_top_atlas_at_covered',
  'mark_cell_visited',
  'open_opponent_site_cell_pick',
  'perceval_draw_rearm',
  'conjure_under_site',
  'heal_player',
  'convert_watch_target_to_rubble',
  'gain_life',
  'lose_life',
  'grant_mana',
  'sacrifice_draw',
  'summon_token_at_locations',
  'grant_status_each',
  'draw',
  'bind_turn_lifespan',
  'grant_threshold',
  'damage_random_in_scope',
  'damage_bearer_per_artifact_in_scope',
  'teleport_to_random_site_or_void_top',
  // Vaults of Zul. Flags the entering Avatar; the server PASS_TURN reducer
  // consumes it (pass-through). Fired by the server's on_enter_location hook.
  'skip_next_turn',
  // Howl from Beyond. Deal the top spells one per outer-column void; Voidwalk
  // minions / Monsters draw, the rest banish. Fully automatic (no pick); the
  // synced classifier + server pile orientation own the deal. The cosmetic
  // face-up deal overlay is client-local polish and does not run under auth.
  'deal_to_outer_voids_draw_voidwalk',
  // Wiccan Tools. The bearer gains Spellcaster + a threshold waiver this turn.
  // Server state (_spellcasterNoThresholdThisTurn + the spellcaster status);
  // the client affordability reads the folded field verbatim.
  'grant_spellcaster_no_threshold_this_turn',
  // Overflow / Landmass. The extra site play this turn is server state
  // (siteGrants + siteGrantRestriction, consumed at the site-cast commit,
  // cleared at turn end); the client's site-play gate reads the folded
  // allowance verbatim (siteGrantInfo).
  'grant_site_placement',
  // The Apex of Babel. The one-shot free-cast window is server state
  // (freeCastGrant); the server prices the next cast 0 and consumes the grant
  // at the commit. The client affordability gate + hand glow read the folded
  // flag verbatim (isFreeCastActive).
  'cast_spell_for_free',
  // Salmon of Knowledge's granted ability. The d20 is a SEEDED server roll
  // (never a local Math.random); the tap cost, the banish and the 3-spell
  // draw all resolve server-side and fold from the diff.
  'roll_banish_source_draw',
  // Kairos the Archivist's Genesis. The synced applyArchiveRealmState runs
  // against the server adapter (SERVER_DISPATCH_OPS), stamping _realmArchive on
  // the draft card where the server-autonomous Deathrite restore reads it. It
  // asks the player nothing, so it belongs here and not in SERVER_CHOICE_OPS,
  // where it was reached only through the lead-op branch of
  // entryIsSilentPrefixChoice.
  'archive_realm_state',
  'reveal_opponent_top_summon_here_or_bottom', // Lilith
  'steal_random_spell_from_opponent_hand',     // Pith Imp
]);

// Every draw variant is now delegable: the server draw op resolves dynamic
// counts (count spec via scope resolution), `from:'bottom'`, and owner-locked
// draws (unit hands via drawOwnerLockedFor). See the server's
// automationService resolveEntry. The former entryHasNonDelegableDraw gate
// (which kept owner/from/count draws client-resolved and double-fired against
// the server hooks) is gone.

// Ops the SERVER resolves via a player-CHOICE round-trip: the trigger intent
// makes the server write a durable pendingChoice; the client renders the picker
// from the projected pendingChoice and replies with chooseIntent. Mirror of the
// server's TARGET_CHOICE_OPS.
export const SERVER_CHOICE_OPS = new Set([
  'damage_each_at_location', // location pick, optionally one random recipient
  'damage_target',
  'kill_target',
  'grant_status_to_target',
  'buff_target',
  // Single target op + a `draw` followup. Both already emit (TARGET_CHOICE_OPS) and
  // resolve (RESOLVE_TARGET_OPS) server-side; only the delegation gate was missing.
  'summon_token_at_target', // Gift of the Frog
  'flood_target_site',      // Geyser
  // Chained: the server emits a target pick THEN a cell pick (Blink / Avatar of
  // Air). authChoice renders each from the mirror's pendingChoice in turn.
  'teleport_target',
  // Chained: site pick THEN an adjacent-unit pick, pulled into the site (Riptide).
  'pull_to_target_site',
  // Kiss of Judas: pick the enemy Avatar → mark undefendable this turn.
  'tag_avatar_undefendable',
  // Fire Harpoons!: pick a minion above/below an adjacent water site → damage + pull to
  // caster, then draw. Server emit injects the legal water-site cells; draw rides as a
  // followup. The client skips its local startSiteRelativeDamage under the choice gate.
  'damage_site_relative_target',
  // Draco Corvus (activated): pick a tapped minion → the source teleports onto its cell
  // and the target is buried underground (non-Burrowing dies). Rides the activated
  // target-choice path; the client skips its local swoop dispatch under the choice gate.
  'swoop_and_burrow_target',
  // Enchantress: pick an aura → it becomes an aura minion (power = cost) until the
  // caster's next turn. Server writes the SET_ANIMATE wire shape; the reconciler's
  // animate fold renders it. Declinable ("may"). The server marks the prompt optional.
  'animate_target_aura',
  // Scry cluster (Riddle Sphinx / Seer / Cradle of Etherrum): the server
  // reveals its pile-top BY ID (SET_REVEALED) and emits a keep/bottom modal
  // carrying `display.cardId`; the client modal renders that exact card,
  // orientation-independent. Cradle's `cast_from_top` is a STANDING server
  // grant derived at cast time (castAdapter.castFromTopGrantFor); the client
  // appends a local Summon affordance to the LOOK modal and commits via a
  // `fromDeckTop` cast intent.
  'open_scry_top',
  'seer_scry_choice',
  // Cemetery → deck pile picks (server pendingChoice kind:'pile'; the client
  // opens its pile multi-select on the projected prompt. Server_pile_choice).
  'shuffle_from_cemetery_into_deck',   // Pendragon Legacy
  'return_from_cemetery_to_deck_bottom', // Return to Nature
  // Sphinx Sarcophagus: banish two identically-named cemetery spells → draw.
  // A pair prompt (pc.pair). The commit must be two same-named ids; declining
  // fizzles the whole Genesis (the draw is contingent on the cost).
  'banish_pair_draw',
  // Xeraphine Konrul: choose three dead minions → one returns to hand, two
  // banish. A CHAINED pair of pile prompts (choose-3 → return-1).
  'cemetery_rites',
  // Feign Death: pick an allied minion → its Deathrite fires in-band (it stays
  // in play); the Stealth grant + draw ride the same pick.
  'trigger_target_deathrite',
  // Valley of Delight's Genesis: a modal element pick → threshold grant with
  // the entry's duration (Annual Fair's activated twin rides the modal-ability
  // gate below).
  'choice_grant_threshold',
  // Black Mass: a top-N spellbook WINDOW pick (zone 'spellbook_window'). Draw
  // up to 3 distinct-named eligibles, the rest of the window bottoms out.
  'search_top_draw_rest_to_bottom',
  // Archangel Gabriel: pick another allied minion → its controller draws a
  // spell only the pick can cast (castLockOwnerId rides the diff).
  'draw_owner_locked_to_target',
  // Searing Truth: a target-player modal → that seat draws two REVEALED spells
  // and takes death-blow-aware Avatar damage equal to the higher cost.
  'force_reveal_draw_damage',
  // Gift of the Raven: pick an allied minion → a one-shot server-only lifelink
  // grant (_strikeLifelink); server combat's on_damage_dealt manifest heals the
  // controller by the damage dealt to a unit. Draw rides as a followup.
  'install_on_strike_lifelink',
  // Thunderstorm: "you may move it one step." An OPTIONAL server cell pick over
  // the newly covered strips; the resume commits the intersection-anchor move
  // (pos rides the diff; the client re-derives _auraAnchor from it).
  'move_aura_one_step',
  // Wildfire: the after_each_turn damage + spread resolve wholly server-side
  // (one trigger intent); the controller answers the destination cell prompt.
  // The visited list lives server-only (_visitedCells); no fresh ground → the
  // server dispels. Delegated via the aura-mover gate in turnPhaseRunner's
  // silent turn-trigger path (the entry is otherwise all-silent locally).
  'move_aura_to_unvisited_adjacent',
  // Flanking Maneuver: a THREE-stage server chain. Source cell (allies) →
  // knight's-move destination → "any number" BOARD pile subset (zone 'board');
  // the commit teleports the picked group together, then the chained draw.
  'group_teleport_knight',
  // Bladedancer: the on_strike step is a server movement-reach cell prompt
  // (once per turn via the server card's _usedThisTurn); the follow-up attack
  // offer is client UX riding the prompt's tag/sourceId. The declaration
  // itself goes through the normal server-validated combat intents.
  'bladedancer_step',
  // Pact with the Devil: a caster-aware cost modal (sacrifice the caster /
  // lose half your life → draw 3). Delegated by the BESPOKE pact gates (both
  // cast pipelines) which thread casterId on the intent. This listing is the
  // coverage accounting; the generic choice gate never reaches it.
  'pact_sacrifice_draw',
  // Feast for Crows: a name-a-spell modal (promptKind 'name_spell') over the
  // whole spell catalog; the resume banishes every copy of the named card
  // from the opponent's hand, spellbook and cemetery and shuffles their
  // spellbook. Only the server holds those hidden-zone identities, so the
  // entire effect is server-owned (the client renders the picker through
  // the searchable pile dialog. foldNameSpellPromptFromMirror).
  'name_spell_banish_from_opponent',
  'ally_step_strike', // Leap Attack / Whirling Blades
  'grapple_shot',
]);

// TARGET-choice pivots. The exact mirror of the server's TARGET_CHOICE_OPS
// keys (automationChoice.js). Only these ops may sit BEHIND a silent prefix
// (the server's splitChoiceEntry resolves the prefix then emits the pivot);
// the other SERVER_CHOICE_OPS families key off effect[0] server-side and are
// lead-only.
export const SERVER_TARGET_CHOICE_OPS = new Set([
  'damage_target',
  'kill_target',
  'grant_status_to_target',
  'buff_target',
  'summon_token_at_target',
  'flood_target_site',
  'teleport_target',
  'pull_to_target_site',
  'tag_avatar_undefendable',
  'damage_site_relative_target',
  'swoop_and_burrow_target',
  'animate_target_aura',
  'trigger_target_deathrite',
  'draw_owner_locked_to_target',
  'install_on_strike_lifelink',
  'ally_step_strike', // Leap Attack / Whirling Blades
  'grapple_shot',
]);

// The generalized choice-entry shape the server owns (splitChoiceEntry mirror):
// [silentPrefix..., choicePivot, followups...]. A lead-pivot entry (prefix [])
// keeps the old behavior for every existing choice card; a silent prefix (Mad
// Dash's draw) is resolved server-side before the prompt is written.
export function entryIsSilentPrefixChoice(entry) {
  const effects = entry?.effect || [];
  if (effects.length === 0) return false;
  if (effects[0].op === 'damage_each_at_location') return effects.length === 1;
  if (SERVER_CHOICE_OPS.has(effects[0].op)) {
    return effects.slice(1).every((e) => SERVER_CHOICE_FOLLOWUP_OPS.has(e.op));
  }
  let i = 0;
  while (i < effects.length && !SERVER_TARGET_CHOICE_OPS.has(effects[i].op)
      && SERVER_SILENT_OPS.has(effects[i].op)) i += 1;
  return i > 0 && i < effects.length
    && SERVER_TARGET_CHOICE_OPS.has(effects[i].op)
    && effects.slice(i + 1).every((e) => SERVER_CHOICE_FOLLOWUP_OPS.has(e.op));
}

// An entry the SERVER resolves end to end: every op is server-silent. This is
// the shape the delegation gates already test inline (automationRuntime's
// silent gate, realmTriggers, the combat gate, serverOwnsCastEntry).
//
// INVARIANT: this predicate must stay a SUBSET of the server's entryServerOwned
// (valkenhall-server/src/match/automationService.js). A client predicate WIDER
// than the server's is worse than no gate at all. The emitted intent is
// filtered out server-side, nothing stamps firedTriggers, and the effect is
// silently lost instead of merely resolving twice.
export function serverOwnsSilentEntry(entry) {
  const effects = entry?.effect || [];
  return effects.length > 0 && effects.every((e) => SERVER_SILENT_OPS.has(e.op));
}

// The delegation test for a turn trigger (on_turn_start / on_turn_end /
// after_each_turn): all-server-silent, or the silent-prefix choice shape.
export function serverOwnsTurnTriggerEntry(entry) {
  return serverOwnsSilentEntry(entry) || entryIsSilentPrefixChoice(entry);
}

// on_draw entries the SERVER owns (drawTriggers fan-out mirror): Chains of
// Prometheus' tap + Iron Maiden's lose_life (fixed or counter-driven). The
// client's fireOnDraw skips these under auth so they resolve once.
export const SERVER_ON_DRAW_OPS = new Set(['tap_strongest_minion', 'lose_life']);

// S3. Trigger FAMILIES the server fires autonomously from its own commits
// (SET_EQUIPMENT → on_pickup/artifact Genesis, the move commit → on_stop /
// on_surface, the cast commit → on_summon_here, the combat manifest → on_kill /
// on_damage_taken, the draw fan-out → on_draw, every death realization → the
// deathTriggers fan-out). Mirror of the server's SERVER_AUTONOMOUS_TRIGGERS:
// the router DROPS client-borne trigger intents for these, so under auth the
// client must neither emit them nor run its local resolution/"may" prompts.
// The server stages a durable confirm modal instead.
//
// on_death joined for qa#106: the silent-delegation gate's intent resolved a
// Deathrite ON TOP of the server's own fan-out at the manual play→cemetery
// move, so Kettletop Leprechaun drew two sites per manual kill.
export const SERVER_AUTONOMOUS_TRIGGERS = new Set([
  'on_pickup', 'on_surface', 'on_stop', 'on_summon_here', 'on_kill', 'on_draw', 'on_damage_taken',
  'on_death',
]);

export function serverOwnsOnDrawEntry(entry) {
  const effects = entry?.effect || [];
  return effects.length > 0 && effects.every((e) => SERVER_ON_DRAW_OPS.has(e.op));
}

// Activated AVATAR abilities the server resolves as a CELL pick (mirror of the
// server's CELL_CHOICE_ABILITIES). When one leads an activated entry under auth,
// the client delegates (emits the ability intent + skips the local dispatch) and
// authChoice renders the server's cell pick.
export const SERVER_ABILITY_CELL_OPS = new Set([
  'pathfinder_tap',
  'geomancer_replace_rubble',
  'avatar_of_water_flood',
  'waveshaper_flood',
  'sparkmage_shock',
  'damage_each_at_location',
  // Flamecaller: the banish-all-dead-fire-minions cost pays at the server emit;
  // the trail cell prompt resolves via the shared projectile scan (flame_blast).
  'flamecaller_fire_blast',
]);

// Activated AVATAR abilities the server resolves as a MODAL pick (mirror of the
// server's MODAL_CHOICE_ABILITIES). When one leads an activated entry under auth,
// the client delegates (emits the ability intent + skips the local dispatch) and
// authChoice renders the server's modal prompt.
export const SERVER_ABILITY_MODAL_OPS = new Set([
  'witch_curse',
  'persecutor_action',
  // Annual Fair "(1) → Gain (A), (E), (F), or (W) this turn".
  'choice_grant_threshold',
]);

// Activated abilities the server resolves as a PILE pick (kind:'pile'). The
// client delegates + renders the pick via the server_pile_choice session on
// the prompt's zone (collection / cemetery).
export const SERVER_ABILITY_PILE_OPS = new Set([
  // Erik's Curiosa: rip to pieces (banish self) → pick a collection card to hand.
  'draw_from_collection',
]);

// Activated abilities the server resolves DIRECTLY (no player choice at all).
// the client emits the ability intent and skips its local dispatch; the diff
// renders the result.
export const SERVER_ABILITY_DIRECT_OPS = new Set([
  // Pnakotic Manuscript's granted tap: reveal + draw the top spell; the bearer
  // takes damage equal to its cost.
  'reveal_draw_damage_bearer',
  // Caelestis: install the "next Dragon cast here costs (0) this turn" ledger
  // entry (set_to + max_uses_per_turn, anchored to the source's live cell).
  // Static modify_cast_cost auras (Templar / Dragonlord's Lair) are untouched.
  // this gate fires only for ACTIVATED entries.
  'modify_cast_cost',
  // Avatar of Fire: the tap stamps the server ignite window (_fireballIgnite);
  // the client keeps its local hand-glow flag as pure UX. Each fireball cast
  // then sends the bespoke 'fireball_shot' ability intent. The server emits
  // the projectile prompt and resolves the 4 + 2-splash damage (fireball_blast).
  'avatar_of_fire_ignite',
]);

// Ops a server-choice entry may carry AFTER its lead target op. The server runs
// them on the pick (tap the picked target; draw standalone for the caster). The
// client delegates a multi-op entry only when its lead is a SERVER_CHOICE_OP and
// every following op is here. Mirror of the server's FOLLOWUP_OPS.
export const SERVER_CHOICE_FOLLOWUP_OPS = new Set([
  'tap_target',
  'draw',
  'draw_if_target_on_enemy_site',
  // Riddle Sphinx: rides the scry resume. The OPPONENT gets a swap modal after
  // the keep/bottom pick, then the caster's draw fires off the final pile order.
  'opponent_may_swap_pile_ends',
  // Feign Death: the Stealth grant rides the deathrite pick (same target).
  'grant_status_to_target',
  // Bladedancer: the per-turn key burns inside the step resume server-side
  // (the entry's trailing mark_used_this_turn is subsumed, not re-applied).
  'mark_used_this_turn',
]);

let _seq = 0;
// A client-generated idempotency key so a re-sent trigger (reconnect replay,
// double-send) is deduped on the SERVER spine, never via a client-local Set. The
// key is unique per emit; the server records it and drops the second resolution.
function nextKey(triggerType, sourceId) {
  _seq += 1;
  return `auto-${triggerType}-${sourceId}-${Date.now()}-${_seq}`;
}

// A fired card trigger (on_strike / on_kill / on_damage_* / on_play / on_death).
// `watchTargetId` is the affected card (defender / killed / source / damaged
// site) the entry's condition + watch-target ops scope against.
export function triggerIntent(sourceId, triggerType, { watchTargetId = null, amount = null, casterId = null } = {}) {
  return {
    domain: 'automation',
    kind: 'trigger',
    sourceId,
    triggerType,
    watchTargetId,
    amount,
    // Pact with the Devil: the cast pipeline's locked-in caster (the unit that
    // cast the spell). The server validates it is the seat's own in-play unit.
    casterId,
    idempotencyKey: nextKey(triggerType, sourceId),
  };
}

// Resume a pending player-choice prompt (server pendingChoice). `promptId` is the
// server-issued id projected on the prompt; `choiceId` is the picked candidate's
// instanceId (the server validates seat + membership before resuming).
export function chooseIntent(promptId, choiceId, seat) {
  return {
    domain: 'automation',
    kind: 'choose',
    promptId,
    choiceId,
    seat,
    idempotencyKey: nextKey('choose', promptId),
  };
}

// Resume a pending PILE choice (server pendingChoice kind:'pile'). `choiceIds`
// is the picked id LIST (empty = a legal "none" commit for up-to-N prompts);
// null declines an optional prompt.
export function choosePileIntent(promptId, choiceIds, seat) {
  return {
    domain: 'automation',
    kind: 'choose',
    promptId,
    choiceIds: Array.isArray(choiceIds) ? [...choiceIds] : null,
    seat,
    idempotencyKey: nextKey('choose-pile', promptId),
  };
}

// Resume a pending CELL choice (server pendingChoice kind:'cell'). `cell` is the
// picked {col,row}; the server validates it against the recorded legal cells.
// A null cell declines an optional prompt.
export function chooseCellIntent(promptId, cell, seat) {
  return {
    domain: 'automation',
    kind: 'choose',
    promptId,
    cell: cell ? { col: cell.col, row: cell.row } : null,
    seat,
    idempotencyKey: nextKey('choose-cell', promptId),
  };
}
